import { Shape, Path } from 'three';
import { Part, Flange, FlangeId, EdgeKey } from './types';
import { bendReliefDimensions } from './validate';
import { findActiveMiterPairs, findCandidateAt, MiterCandidate } from './miterPairs';

interface Notch {
  pos: number;     // start coordinate along the edge direction
  width: number;   // size along the edge direction
  depth: number;   // size perpendicular into the flange
}

// For each edge of the flange, collect the relief notches induced by partial
// child coverage. The notch matches what unfold.ts produces in flat (rectangular
// version — the obround arc is purely a flat-pattern detail used by laser CAM).
function collectNotches(part: Part, flange: Flange): Record<EdgeKey, Notch[]> {
  const T = part.thickness;
  const result: Record<EdgeKey, Notch[]> = { '+x': [], '-x': [], '+y': [], '-y': [] };
  for (const cid of flange.children) {
    const c = part.flanges[cid];
    if (!c?.attachment) continue;
    const O = c.attachment.offset, Wc = c.width;
    const relief = bendReliefDimensions(T, c.attachment.bendRadius);
    const edgeLen = (c.attachment.edge === '+x' || c.attachment.edge === '-x') ? flange.width : flange.length;
    if (O > 1e-3) {
      result[c.attachment.edge].push({ pos: O - relief.width, width: relief.width, depth: relief.depth });
    }
    if (edgeLen - (O + Wc) > 1e-3) {
      result[c.attachment.edge].push({ pos: O + Wc, width: relief.width, depth: relief.depth });
    }
  }
  return result;
}

// Build the 2D outline of `flangeId` in its own local frame, accounting for
// miter cuts at the bend-tangent corners and relief notches at child-attaching
// edges. Returns a THREE.Shape suitable for ExtrudeGeometry.
//
// Local axes: x = flange length (perpendicular to bend tangent, pointing away
// from parent), y = flange width (along bend tangent / parent edge direction).
// Walked CCW so ExtrudeGeometry produces outward-facing normals.
// `pairs` may be passed in to avoid recomputing for every flange in a scene; if
// omitted, pairs are computed on demand.
export function buildFlangeShape(part: Part, flangeId: FlangeId, pairs?: MiterCandidate[]): Shape {
  const f = part.flanges[flangeId];
  const shape = new Shape();
  if (!f) { shape.moveTo(0, 0); return shape; }

  const L = f.length, W = f.width;
  const gap = Math.max(0, part.miterGap ?? 0);

  // Miter cuts use the 3D pair detection (siblings on perpendicular edges OR caps
  // on opposite walls that meet at a top corner). They require length ≤ width and
  // gap ≤ width − length to fit a clean diagonal in the flange's local frame.
  let miterLow = false, miterHigh = false;
  if (f.attachment?.miterCorner && L <= W + 1e-3 && (gap === 0 || gap <= W - L - 1e-6)) {
    const activePairs = pairs ?? findActiveMiterPairs(part);
    if (findCandidateAt(activePairs, flangeId, 'low')) miterLow = true;
    if (findCandidateAt(activePairs, flangeId, 'high')) miterHigh = true;
  }

  const notches = collectNotches(part, f);

  // CCW walk:
  // 1. start
  shape.moveTo(0, miterLow ? gap : 0);

  // 2. BOTTOM edge (y = 0) or LOW miter diagonal
  if (miterLow) {
    // Diagonal removes the entire bottom triangle: from (0, gap) to (L, L+gap).
    shape.lineTo(L, L + gap);
  } else {
    // Walk +x along y=0, detouring around -y notches.
    const sortedBottom = [...notches['-y']].sort((a, b) => a.pos - b.pos);
    for (const n of sortedBottom) {
      shape.lineTo(n.pos, 0);
      shape.lineTo(n.pos, n.depth);
      shape.lineTo(n.pos + n.width, n.depth);
      shape.lineTo(n.pos + n.width, 0);
    }
    shape.lineTo(L, 0);
  }

  // 3. RIGHT (far) edge (x = L), walk +y, detouring around +x notches.
  const rightStart = miterLow ? L + gap : 0;
  const rightEnd = miterHigh ? W - L - gap : W;
  const sortedRight = [...notches['+x']]
    .filter((n) => n.pos >= rightStart - 1e-6 && n.pos + n.width <= rightEnd + 1e-6)
    .sort((a, b) => a.pos - b.pos);
  for (const n of sortedRight) {
    shape.lineTo(L, n.pos);
    shape.lineTo(L - n.depth, n.pos);
    shape.lineTo(L - n.depth, n.pos + n.width);
    shape.lineTo(L, n.pos + n.width);
  }
  shape.lineTo(L, rightEnd);

  // 4. TOP edge (y = W) or HIGH miter diagonal
  if (miterHigh) {
    shape.lineTo(0, W - gap);
  } else {
    // Walk -x along y=W, detouring around +y notches (decreasing x).
    const sortedTop = [...notches['+y']].sort((a, b) => b.pos - a.pos);
    for (const n of sortedTop) {
      shape.lineTo(n.pos + n.width, W);
      shape.lineTo(n.pos + n.width, W - n.depth);
      shape.lineTo(n.pos, W - n.depth);
      shape.lineTo(n.pos, W);
    }
    shape.lineTo(0, W);
  }

  // 5. LEFT edge (x = 0) — bend tangent for child flanges, no notches.
  shape.lineTo(0, miterLow ? gap : 0);

  // 6. Holes — added as inner paths (subtracted from the extruded solid).
  for (const h of part.holes) {
    if (h.flangeId !== flangeId) continue;
    const path = new Path();
    if (h.kind === 'round') {
      // Clockwise so it cuts from the CCW outer.
      path.absarc(h.x, h.y, h.diameter / 2, 0, Math.PI * 2, true);
    } else {
      // Slot: rectangle with two semicircular ends along the local +X (rotated by `rotation`).
      const r = h.diameter / 2;
      const len = Math.max(h.length ?? h.diameter, h.diameter);
      const halfStraight = (len - h.diameter) / 2; // 0 if length == diameter
      const rot = ((h.rotation ?? 0) * Math.PI) / 180;
      const cs = Math.cos(rot), sn = Math.sin(rot);
      const at = (lx: number, ly: number) => ({ x: h.x + lx * cs - ly * sn, y: h.y + lx * sn + ly * cs });
      // Walk CW around the slot (so it acts as a hole vs the CCW outer).
      const p0 = at(-halfStraight, -r); // bottom-left of straight
      const p1 = at(halfStraight, -r);  // bottom-right
      const c1 = at(halfStraight, 0);   // right cap center
      const p3 = at(-halfStraight, r);  // top-left
      const c0 = at(-halfStraight, 0);  // left cap center
      path.moveTo(p0.x, p0.y);
      path.lineTo(p1.x, p1.y);
      // CCW arcs that bulge OUTWARD from the slot's interior (toward +x for the
      // right cap, −x for the left cap). Without this the arc sweeps through the
      // interior and the path self-intersects.
      path.absarc(c1.x, c1.y, r, rot - Math.PI / 2, rot + Math.PI / 2, false);
      path.lineTo(p3.x, p3.y);
      path.absarc(c0.x, c0.y, r, rot + Math.PI / 2, rot + Math.PI * 1.5, false);
    }
    shape.holes.push(path);
  }

  return shape;
}
